import type {
  AgencyModel,
  FeatureConfigurationResponse,
  FeatureDefinition,
} from '@urbanx/agx.agencies.client';
import { Feature, LicenceDetailsState } from '@urbanx/agx.agencies.client';
import type { DropdownOption, IColumnConfig } from '@urbanx/agx.ui-components2';

export type FeatureKey = Feature;
export type FeatureLevel = 'global' | 'state' | 'agency';
export type FeatureState = LicenceDetailsState;
export type AgencyScope = 'all' | FeatureState;
export type FeatureChoice = 'inherit' | 'on' | 'off';

export const allStates: FeatureState[] = Object.values(LicenceDetailsState);

export const tableColumns: (string | IColumnConfig)[] = [
  { name: 'Feature', maxColWidth: '600px' },
  { name: 'Status', maxColWidth: '280px' },
  { name: 'Set to' },
];

export const levelTabs: { id: FeatureLevel; label: string }[] = [
  { id: 'global', label: 'Global' },
  { id: 'state', label: 'State' },
  { id: 'agency', label: 'Agency' },
];

export const stateOptions = allStates.map((state) => ({ label: state, value: state }));

export const choiceOptions: DropdownOption[] = [
  { label: 'Inherit', value: 'inherit' },
  { label: 'On', value: 'on' },
  { label: 'Off', value: 'off' },
];

export const concreteChoiceOptions = choiceOptions.filter(({ value }) => value !== 'inherit');

export const createEmptyConfiguration = (): FeatureConfigurationResponse => ({
  global: {},
  states: {},
  agencies: {},
  agencyStates: {},
});

export const cloneConfiguration = (
  configuration?: FeatureConfigurationResponse
): FeatureConfigurationResponse => structuredClone(configuration ?? createEmptyConfiguration());

export const getSelectedAgency = (agencies: AgencyModel[] | undefined, selectedAgencyId: string) =>
  (agencies ?? []).find((agency) => agency.id === selectedAgencyId);

export const getAgencyStates = (agency?: AgencyModel): FeatureState[] =>
  (agency?.states ?? [])
    .map(String)
    .filter((state): state is FeatureState => allStates.includes(state as FeatureState));

export const getAgencyOptions = (agencies: AgencyModel[] | undefined): DropdownOption[] =>
  (agencies ?? [])
    .filter((agency) => agency.id)
    .map((agency) => ({
      label: agency.name ?? 'Unnamed agency',
      value: agency.id!,
      dataTestId: `featureToggleAgency-option-${agency.id}`,
    }));

export const isPlatformFeature = (definition: FeatureDefinition) =>
  !definition.applicableStates?.length;

export const appliesTo = (definition: FeatureDefinition, state: FeatureState) =>
  isPlatformFeature(definition) ||
  definition.applicableStates?.some((applicableState) => String(applicableState) === state) ===
    true;

const hasValue = (values: { [key: string]: boolean } | undefined, feature: FeatureKey) =>
  Object.hasOwn(values ?? {}, feature);

export const getFeatureChoice = (
  configuration: FeatureConfigurationResponse,
  level: FeatureLevel,
  feature: FeatureKey,
  state?: FeatureState,
  agencyId?: string,
  agencyScope?: AgencyScope
): FeatureChoice => {
  if (level === 'global') return configuration.global?.[feature] === true ? 'on' : 'off';

  if (level === 'state') {
    const stateValues = state ? configuration.states?.[state] : undefined;
    if (!hasValue(stateValues, feature)) return 'inherit';
    return stateValues?.[feature] ? 'on' : 'off';
  }

  const values =
    agencyScope === 'all'
      ? configuration.agencies?.[agencyId ?? '']
      : configuration.agencyStates?.[agencyId ?? '']?.[agencyScope ?? ''];

  if (!hasValue(values, feature)) return 'inherit';
  return values?.[feature] ? 'on' : 'off';
};

const choiceValue = (choice: FeatureChoice) => choice === 'on';

export const setFeatureChoice = (
  configuration: FeatureConfigurationResponse,
  level: FeatureLevel,
  feature: FeatureKey,
  choice: FeatureChoice,
  platformFeature: boolean,
  state?: FeatureState,
  agencyId?: string,
  agencyScope?: AgencyScope
): FeatureConfigurationResponse => {
  const next = cloneConfiguration(configuration);

  if (level === 'global') {
    next.global ??= {};
    if (choice === 'off') delete next.global[feature];
    else next.global[feature] = true;
    return pruneConfiguration(next);
  }

  if (level === 'state' && state) {
    next.states ??= {};
    next.states[state] ??= {};
    if (choice === 'inherit' || (!platformFeature && choice === 'off')) {
      delete next.states[state][feature];
    } else {
      next.states[state][feature] = choiceValue(choice);
    }
    return pruneConfiguration(next);
  }

  if (level === 'agency' && agencyId) {
    if (agencyScope === 'all') {
      next.agencies ??= {};
      next.agencies[agencyId] ??= {};
      if (choice === 'inherit') delete next.agencies[agencyId][feature];
      else next.agencies[agencyId][feature] = choiceValue(choice);
    } else if (agencyScope) {
      next.agencyStates ??= {};
      next.agencyStates[agencyId] ??= {};
      next.agencyStates[agencyId][agencyScope] ??= {};
      if (choice === 'inherit') delete next.agencyStates[agencyId][agencyScope][feature];
      else next.agencyStates[agencyId][agencyScope][feature] = choiceValue(choice);
    }
  }

  return pruneConfiguration(next);
};

export interface ResolvedFeature {
  value: boolean;
  source: string;
  sourceLevel: 'global' | 'state' | 'agency' | 'agencyState';
}

export const resolveStateFeature = (
  configuration: FeatureConfigurationResponse,
  state: FeatureState,
  feature: FeatureKey,
  platformFeature: boolean
): ResolvedFeature => {
  if (hasValue(configuration.states?.[state], feature)) {
    return {
      value: configuration.states?.[state]?.[feature] === true,
      source: `${state} override`,
      sourceLevel: 'state',
    };
  }

  if (platformFeature && hasValue(configuration.global, feature)) {
    return {
      value: configuration.global?.[feature] === true,
      source: 'Global',
      sourceLevel: 'global',
    };
  }

  return {
    value: false,
    source: 'Default off',
    sourceLevel: platformFeature ? 'global' : 'state',
  };
};

export const resolveAgencyFeature = (
  configuration: FeatureConfigurationResponse,
  agencyId: string,
  state: FeatureState,
  feature: FeatureKey,
  platformFeature: boolean
): ResolvedFeature => {
  if (hasValue(configuration.agencyStates?.[agencyId]?.[state], feature)) {
    return {
      value: configuration.agencyStates?.[agencyId]?.[state]?.[feature] === true,
      source: `Agency ${state} override`,
      sourceLevel: 'agencyState',
    };
  }

  if (platformFeature && hasValue(configuration.agencies?.[agencyId], feature)) {
    return {
      value: configuration.agencies?.[agencyId]?.[feature] === true,
      source: 'Agency-wide override',
      sourceLevel: 'agency',
    };
  }

  return resolveStateFeature(configuration, state, feature, platformFeature);
};

export const resolveAgencyWideFeature = (
  configuration: FeatureConfigurationResponse,
  agencyId: string,
  states: FeatureState[],
  feature: FeatureKey
): ResolvedFeature => {
  const resolved = states.map((state) =>
    resolveAgencyFeature(configuration, agencyId, state, feature, true)
  );
  const first = resolved[0];

  if (!first) return { value: false, source: 'No licensed states', sourceLevel: 'global' };

  const sourceLevel = (['agencyState', 'agency', 'state', 'global'] as const).find((level) =>
    resolved.some((featureValue) => featureValue.sourceLevel === level)
  )!;

  if (resolved.some(({ value }) => value !== first.value)) {
    const enabled = resolved.filter(({ value }) => value).length;
    return {
      value: false,
      source: `Mixed: ${enabled} on, ${resolved.length - enabled} off`,
      sourceLevel,
    };
  }

  const source = resolved.every(({ source }) => source === first.source)
    ? first.source
    : `All ${first.value ? 'on' : 'off'} across states`;
  return { value: first.value, source, sourceLevel };
};

export const pruneConfiguration = (
  configuration?: FeatureConfigurationResponse
): FeatureConfigurationResponse => {
  const next = cloneConfiguration(configuration);

  for (const state of Object.keys(next.states ?? {})) {
    if (!Object.keys(next.states?.[state] ?? {}).length) delete next.states?.[state];
  }
  for (const agencyId of Object.keys(next.agencies ?? {})) {
    if (!Object.keys(next.agencies?.[agencyId] ?? {}).length) delete next.agencies?.[agencyId];
  }
  for (const agencyId of Object.keys(next.agencyStates ?? {})) {
    for (const state of Object.keys(next.agencyStates?.[agencyId] ?? {})) {
      if (!Object.keys(next.agencyStates?.[agencyId]?.[state] ?? {}).length) {
        delete next.agencyStates?.[agencyId]?.[state];
      }
    }
    if (!Object.keys(next.agencyStates?.[agencyId] ?? {}).length) {
      delete next.agencyStates?.[agencyId];
    }
  }

  return next;
};

export interface DescendantOverridePreviewItem {
  id: string;
  type: 'state' | 'agency';
  label: string;
  value: 'On' | 'Off' | 'Varies by state';
}

const displayValue = (value: ResolvedFeature): DescendantOverridePreviewItem['value'] =>
  value.source.startsWith('Mixed:') ? 'Varies by state' : value.value ? 'On' : 'Off';

export const getDescendantOverridePreview = (
  configuration: FeatureConfigurationResponse,
  agencies: AgencyModel[],
  level: FeatureLevel,
  feature: FeatureKey,
  platformFeature: boolean,
  state?: FeatureState,
  agencyId?: string,
  agencyScope?: AgencyScope
): DescendantOverridePreviewItem[] => {
  if (level === 'global') {
    const states = allStates
      .filter((candidate) => hasValue(configuration.states?.[candidate], feature))
      .map((candidate) => ({
        id: `state-${candidate}`,
        type: 'state' as const,
        label: candidate,
        value: configuration.states?.[candidate]?.[feature] ? ('On' as const) : ('Off' as const),
      }));
    const agencyOverrides = agencies
      .flatMap((agency) => {
        const agencyId = agency.id;
        if (!agencyId) return [];
        const hasOverride =
          hasValue(configuration.agencies?.[agencyId], feature) ||
          getAgencyStates(agency).some((candidate) =>
            hasValue(configuration.agencyStates?.[agencyId]?.[candidate], feature)
          );

        return hasOverride
          ? [
              {
                id: `agency-${agencyId}`,
                type: 'agency' as const,
                label: agency.name ?? 'Unnamed agency',
                value: displayValue(
                  resolveAgencyWideFeature(
                    configuration,
                    agencyId,
                    getAgencyStates(agency),
                    feature
                  )
                ),
              },
            ]
          : [];
      })
      .sort((left, right) => left.label.localeCompare(right.label));

    return [...states, ...agencyOverrides];
  }

  if (level === 'state' && state) {
    return agencies
      .flatMap((agency) => {
        const agencyId = agency.id;
        if (!agencyId || !getAgencyStates(agency).includes(state)) return [];
        const hasOverride =
          hasValue(configuration.agencies?.[agencyId], feature) ||
          hasValue(configuration.agencyStates?.[agencyId]?.[state], feature);

        return hasOverride
          ? [
              {
                id: `agency-${agencyId}`,
                type: 'agency' as const,
                label: agency.name ?? 'Unnamed agency',
                value: displayValue(
                  resolveAgencyFeature(configuration, agencyId, state, feature, platformFeature)
                ),
              },
            ]
          : [];
      })
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  if (level === 'agency' && agencyId && agencyScope === 'all') {
    const agency = agencies.find((candidate) => candidate.id === agencyId);
    return getAgencyStates(agency)
      .filter((candidate) => hasValue(configuration.agencyStates?.[agencyId]?.[candidate], feature))
      .map((candidate) => ({
        id: `agency-state-${candidate}`,
        type: 'state' as const,
        label: candidate,
        value: configuration.agencyStates?.[agencyId]?.[candidate]?.[feature]
          ? ('On' as const)
          : ('Off' as const),
      }));
  }

  return [];
};

export const resetDescendantOverrides = (
  configuration: FeatureConfigurationResponse,
  agencies: AgencyModel[],
  level: FeatureLevel,
  feature: FeatureKey,
  state?: FeatureState,
  agencyId?: string,
  agencyScope?: AgencyScope
) => {
  const next = cloneConfiguration(configuration);

  if (level === 'global') {
    for (const values of Object.values(next.states ?? {})) delete values[feature];
    for (const values of Object.values(next.agencies ?? {})) delete values[feature];
    for (const states of Object.values(next.agencyStates ?? {})) {
      for (const values of Object.values(states)) delete values[feature];
    }
  } else if (level === 'state' && state) {
    for (const agency of agencies) {
      if (!agency.id || !getAgencyStates(agency).includes(state)) continue;
      delete next.agencies?.[agency.id]?.[feature];
      delete next.agencyStates?.[agency.id]?.[state]?.[feature];
    }
  } else if (level === 'agency' && agencyId && agencyScope === 'all') {
    for (const values of Object.values(next.agencyStates?.[agencyId] ?? {})) {
      delete values[feature];
    }
  }

  return pruneConfiguration(next);
};
